import { companyDocumentExpiry } from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  attachDocumentToEstimate,
  deleteRow,
  fetchCompanyDocuments,
  fetchEstimateDocuments,
} from "../../lib/queries";

/**
 * Which company documents go out with this estimate.
 *
 * Jonathon, 2026-08-15: company docs "needs to go hand in hand with what we can
 * add to emails and estimates when needed." This is the estimates half. The
 * licence and the certificate of insurance are what a customer or a general
 * contractor asks for, and the answer today is somebody digging through email.
 *
 * Picked per estimate rather than per job, because the set genuinely differs
 * between two quotes on one house — the metal option carries the metal
 * warranty. The library itself is Tools → Company Docs; nothing is uploaded
 * from here, because a document worth attaching twice is worth naming once.
 *
 * An expired document is flagged and still attachable. The rep at the kitchen
 * table is not the person who can renew a state licence, and refusing to send
 * a warranty brochure because the COI lapsed helps nobody.
 */
export function ProposalDocuments({ estimateId }: { estimateId: string }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const library = useAsync(() => fetchCompanyDocuments(), []);
  const attached = useAsync(
    () => fetchEstimateDocuments(estimateId),
    [estimateId],
  );

  const documents = library.data ?? [];
  const rows = attached.data ?? [];
  const attachedIds = new Map(
    rows.map((row) => [row.company_document_id, row]),
  );
  const today = new Date().toISOString().slice(0, 10);

  const toggle = async (documentId: string) => {
    setBusy(documentId);
    setError(null);
    try {
      const existing = attachedIds.get(documentId);
      if (existing) {
        await deleteRow("estimate_documents", existing.id);
      } else {
        const next =
          rows.reduce((max, row) => Math.max(max, row.sort_order), 0) + 10;
        await attachDocumentToEstimate(estimateId, documentId, next);
      }
      await attached.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="chk-sub">{t("Our paperwork")}</div>
      <ErrorNote error={error} />

      {documents.length === 0 ? (
        <div className="na-hint">
          {t(
            "Nothing in the library yet. Add the licence, the insurance certificate and anything else you send often under Tools → Company Docs.",
          )}
        </div>
      ) : (
        <div className="doclist">
          {documents.map((doc) => {
            const expiry = companyDocumentExpiry(doc, today);
            const on = attachedIds.has(doc.id);
            return (
              <label key={doc.id} className="docpick">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={busy === doc.id}
                  onChange={() => void toggle(doc.id)}
                />
                <span className="docname">{doc.name}</span>
                {doc.category && (
                  <span className="dockind">{doc.category}</span>
                )}
                {expiry && (
                  <span className={expiry.expired ? "expirybad" : "expirysoon"}>
                    {expiry.label}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}

      <div className="na-hint">
        {rows.length === 0
          ? t(
              "None attached. They appear on the customer's copy under “Our paperwork”.",
            )
          : tn(
              rows.length,
              "1 attached, in this order, under “Our paperwork”.",
              "{n} attached, in this order, under “Our paperwork”.",
            )}
      </div>
    </>
  );
}
